/* Aloe Jewels – production & delivery estimate (snippets/aj-delivery.liquid)
 *
 * Rules (block setting, one line per country, days counted from the order day in working days):
 *   FR: 0-2, 2-4, express      → production today…+2, delivery +2…+4, "Express delivery" title
 *   *:  0-2, 4-10              → optional fallback for every other country (omit to hide the block there)
 *
 * Day 0 = today in the store's time zone (or the next working day if today is off / after the cutoff hour).
 */
(() => {
  if (customElements.get('aj-delivery')) return;

  const DAY = 86400000;

  function parseRules(text) {
    const rules = {};
    String(text || '').split(/\n|;/).forEach((line) => {
      const m = line.trim().match(/^([A-Za-z]{2}|\*)\s*:\s*(\d+)\s*-\s*(\d+)\s*,\s*(\d+)\s*-\s*(\d+)\s*(?:,\s*(express))?/i);
      if (!m) return;
      rules[m[1].toUpperCase()] = {
        production: [Number(m[2]), Number(m[3])],
        delivery: [Number(m[4]), Number(m[5])],
        express: Boolean(m[6]),
      };
    });
    return rules;
  }

  // Today's calendar date and hour in the store's time zone, as a UTC-noon Date (safe for day arithmetic)
  function storeToday(timeZone) {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23' })
        .formatToParts(new Date())
        .map((p) => [p.type, p.value])
    );
    return { date: new Date(Date.UTC(+parts.year, +parts.month - 1, +parts.day, 12)), hour: +parts.hour };
  }

  class AjDelivery extends HTMLElement {
    connectedCallback() {
      this.render();
      // Recalculate if the tab stays open past midnight
      this.onVisibility = () => { if (!document.hidden) this.render(); };
      document.addEventListener('visibilitychange', this.onVisibility);
    }

    disconnectedCallback() {
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    render() {
      const rules = parseRules(this.dataset.rules);
      const rule = rules[this.dataset.country] || rules['*'];
      if (!rule) { this.hidden = true; return; }

      const offDays = new Set(String(this.dataset.offDays || '').split(',').filter(Boolean).map(Number));
      const holidays = new Set(String(this.dataset.holidays || '').match(/\d{4}-\d{2}-\d{2}/g) || []);
      const isOff = (d) => offDays.has(d.getUTCDay()) || holidays.has(d.toISOString().slice(0, 10));

      const { date: today, hour } = storeToday(this.dataset.timezone || 'Europe/Lisbon');
      let start = today;
      const cutoff = Number(this.dataset.cutoff);
      if (cutoff > 0 && cutoff < 24 && hour >= cutoff) start = new Date(start.getTime() + DAY);
      while (isOff(start)) start = new Date(start.getTime() + DAY);

      const addWorkingDays = (n) => {
        let d = start;
        for (let i = 0; i < n; i++) {
          d = new Date(d.getTime() + DAY);
          while (isOff(d)) d = new Date(d.getTime() + DAY);
        }
        return d;
      };

      const makeFormatter = (opts) => {
        try { return new Intl.DateTimeFormat(this.dataset.locale, { ...opts, timeZone: 'UTC' }); } catch (e) { return new Intl.DateTimeFormat(undefined, { ...opts, timeZone: 'UTC' }); }
      };
      let formatter = makeFormatter(this.dataset.dateStyle === 'short_month' ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'numeric' });
      // Some languages (e.g. Dutch) write numeric dates with hyphens, which clashes with the range dash → use month names
      if (/-/.test(formatter.format(start))) formatter = makeFormatter({ day: 'numeric', month: 'short' });

      const range = ([min, max]) => {
        const a = formatter.format(addWorkingDays(min));
        const b = formatter.format(addWorkingDays(Math.max(min, max)));
        return a === b ? a : `${a} – ${b}`;
      };

      this.querySelector('[data-step="production"]').textContent = range(rule.production);
      this.querySelector('[data-step="delivery"]').textContent = range(rule.delivery);

      // Standard or "express" title (both are written per country in the locale files)
      this.querySelectorAll('.aj-delivery__title').forEach((el) => {
        el.hidden = el.dataset.variant !== (rule.express ? 'express' : 'standard');
      });

      // Countries without their own wording: show the country name in the visitor's language
      if (this.querySelector('[data-generic-place]')) {
        try {
          const name = new Intl.DisplayNames([this.dataset.locale.split('-')[0]], { type: 'region' }).of(this.dataset.country);
          if (name && name !== this.dataset.country) this.querySelectorAll('.aj-delivery__title strong').forEach((el) => { el.textContent = name; });
        } catch (e) { /* keep Shopify's name */ }
      }

      this.hidden = false;
    }
  }

  customElements.define('aj-delivery', AjDelivery);
})();
